import { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { Play, Pause, Square, UserPlus, Clock, Activity, Eye, Edit2, X, CheckCircle, UserX, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { SelectSingle } from "../../components/SelectSingle";
import { TextArea } from "../../components/TextArea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { relatorioMovimentoOficialService, RelatorioMovimentoOficialResponsavel } from "../../services/relatorioMovimentoOficial";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useUserGroups } from "../../hooks/useUserGroups";
import moment from "moment";
import { SkeletonResponsavel, SkeletonCard, StaggerWrapper, FadeInWrapper } from "../../components/Skeleton";

export function RelatorioMovimentoOficial() {
  const { militar } = useAuth();
  const { hasPermission } = usePermissions();
  const { grupos } = useUserGroups();

  const [responsavelAtual, setResponsavelAtual] = useState<RelatorioMovimentoOficialResponsavel | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingResponsavel, setLoadingResponsavel] = useState(true);
  const [loadingVisaoGeral, setLoadingVisaoGeral] = useState(true);
  const [statusAtual, setStatusAtual] = useState<any>(null);
  const [todosStatus, setTodosStatus] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<"personal" | "manager">("personal");
  const [tempoResponsavel, setTempoResponsavel] = useState("");
  const [temposAtividade, setTemposAtividade] = useState<Record<string, string>>({});

  // Estados para edição de status de outros oficiais
  const [editandoMilitar, setEditandoMilitar] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState("");
  const [editTipoAtividade, setEditTipoAtividade] = useState("");
  const [editGrupo, setEditGrupo] = useState("");
  const [editDescricao, setEditDescricao] = useState("");

  // Modais de confirmação rápida de status
  const [modalStatusAberta, setModalStatusAberta] = useState(false);
  const [militarSelecionado, setMilitarSelecionado] = useState<any>(null);
  const [statusSelecionado, setStatusSelecionado] = useState("");
  const [tipoAtividadeModal, setTipoAtividadeModal] = useState("");
  const [grupoModal, setGrupoModal] = useState("");
  const [descricaoModal, setDescricaoModal] = useState("");

  // Modais de confirmação de pausar/finalizar RMO
  const [modalPausarRMOAberta, setModalPausarRMOAberta] = useState(false);
  const [modalFinalizarRMOAberta, setModalFinalizarRMOAberta] = useState(false);

  // Atualizar tempo como responsável
  useEffect(() => {
    if (!responsavelAtual?.data_hora_inicio || responsavelAtual?.status === "pausado") {
      setTempoResponsavel("");
      return;
    }

    const atualizarTempo = () => {
      const inicio = moment(responsavelAtual.data_hora_inicio);
      const agora = moment();
      const tempoTotalMs = agora.diff(inicio);

      // Subtrair tempo pausado (em milissegundos)
      const tempoPausadoMs = (responsavelAtual.tempo_pausado_segundos || 0) * 1000;
      const tempoAtivoMs = tempoTotalMs - tempoPausadoMs;

      const duration = moment.duration(tempoAtivoMs);
      const horas = Math.floor(duration.asHours()).toString().padStart(2, "0");
      const minutos = duration.minutes().toString().padStart(2, "0");
      const segundos = duration.seconds().toString().padStart(2, "0");
      setTempoResponsavel(`${horas}:${minutos}:${segundos}`);
    };

    atualizarTempo();
    const intervalo = setInterval(atualizarTempo, 1000);

    return () => clearInterval(intervalo);
  }, [responsavelAtual]);

  // Atualizar tempos de atividade de todos os oficiais
  useEffect(() => {
    if (viewMode !== "manager" || todosStatus.length === 0) {
      return;
    }

    const atualizarTempos = () => {
      const novosTempos: Record<string, string> = {};

      todosStatus.forEach((registro) => {
        if (registro.status === "em_atividade" && registro.data_hora_inicio) {
          const inicio = moment(registro.data_hora_inicio);
          const agora = moment();
          const diff = agora.diff(inicio);

          const duration = moment.duration(diff);
          const horas = Math.floor(duration.asHours()).toString().padStart(2, "0");
          const minutos = duration.minutes().toString().padStart(2, "0");
          const segundos = duration.seconds().toString().padStart(2, "0");

          novosTempos[registro.id_militar] = `${horas}:${minutos}:${segundos}`;
        }
      });

      setTemposAtividade(novosTempos);
    };

    atualizarTempos();
    const intervalo = setInterval(atualizarTempos, 1000);

    return () => clearInterval(intervalo);
  }, [todosStatus, viewMode]);

  // Formulário de status
  const [selectedStatus, setSelectedStatus] = useState("");
  const [tipoAtividade, setTipoAtividade] = useState("");
  const [grupoSelecionado, setGrupoSelecionado] = useState("");
  const [descricaoAtividade, setDescricaoAtividade] = useState("");

  const canManageRMO = hasPermission("central.rmo.gerenciar");

  const statusOptions = [
    { value: "ativo", label: "Ativo" },
    { value: "inativo", label: "Inativo" },
    { value: "em_atividade", label: "Em Atividade" },
  ];

  const tipoAtividadeOptions = [
    { value: "grupo", label: "Grupo" },
    { value: "treinamento", label: "Treinamento" },
    { value: "patrulha", label: "Patrulha" },
    { value: "outros", label: "Outros" },
  ];

  useEffect(() => {
    if (canManageRMO) {
      loadResponsavelAtual();
    }
    loadStatusAtual();
    if (canManageRMO && viewMode === "manager") {
      loadTodosStatus();
    }

    // Atualização automática a cada 20 segundos
    const autoUpdateInterval = setInterval(() => {
      if (canManageRMO) {
        loadResponsavelAtual();
      }
      loadStatusAtual();
      if (canManageRMO && viewMode === "manager") {
        loadTodosStatus();
      }
    }, 20000);

    return () => clearInterval(autoUpdateInterval);
  }, [viewMode]);

  const loadResponsavelAtual = async () => {
    try {
      setLoadingResponsavel(true);
      const data = await relatorioMovimentoOficialService.getResponsavelAtual();
      setResponsavelAtual(data);
    } catch (error) {
      console.error("Erro ao carregar responsável:", error);
    } finally {
      setLoadingResponsavel(false);
    }
  };

  const loadStatusAtual = async () => {
    try {
      const data = await relatorioMovimentoOficialService.getStatusAtual();
      setStatusAtual(data);
      if (data) {
        setSelectedStatus(data.status);
        if (data.tipo_atividade) {
          setTipoAtividade(data.tipo_atividade);
        }
        if (data.id_grupo) {
          setGrupoSelecionado(data.id_grupo);
        }
        if (data.descricao_atividade) {
          setDescricaoAtividade(data.descricao_atividade);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar status atual:", error);
    }
  };

  const loadTodosStatus = async () => {
    try {
      setLoadingVisaoGeral(true);
      const data = await relatorioMovimentoOficialService.getAllStatus();
      setTodosStatus(data);
    } catch (error) {
      console.error("Erro ao carregar todos os status:", error);
    } finally {
      setLoadingVisaoGeral(false);
    }
  };

  const handleIniciarRMO = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoOficialService.iniciarRMO();
      setResponsavelAtual(data);
      enqueueSnackbar("RMO iniciado com sucesso!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao iniciar RMO", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePegarRMO = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoOficialService.pegarRMO();
      setResponsavelAtual(data);
      enqueueSnackbar("Você assumiu o RMO!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pegar RMO", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePausarRMO = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoOficialService.pausarRMO();
      setResponsavelAtual(data);
      enqueueSnackbar("RMO pausado!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pausar RMO", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetomarRMO = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoOficialService.retomarRMO();
      setResponsavelAtual(data);
      enqueueSnackbar("RMO retomado!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao retomar RMO", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizarRMO = async () => {
    try {
      setLoading(true);
      await relatorioMovimentoOficialService.finalizarRMO();
      setResponsavelAtual(null);
      enqueueSnackbar("RMO finalizado!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao finalizar RMO", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) {
      enqueueSnackbar("Selecione um status", { variant: "error" });
      return;
    }

    if (selectedStatus === "em_atividade" && !tipoAtividade) {
      enqueueSnackbar("Selecione o tipo de atividade", { variant: "error" });
      return;
    }

    if (tipoAtividade === "grupo" && !grupoSelecionado) {
      enqueueSnackbar("Selecione um grupo", { variant: "error" });
      return;
    }

    if (tipoAtividade === "outros" && !descricaoAtividade) {
      enqueueSnackbar("Descreva a atividade", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await relatorioMovimentoOficialService.updateStatus({
        status: selectedStatus,
        tipo_atividade: selectedStatus === "em_atividade" ? tipoAtividade : undefined,
        id_grupo: tipoAtividade === "grupo" ? grupoSelecionado : undefined,
        descricao_atividade: tipoAtividade === "outros" ? descricaoAtividade : undefined,
      });

      enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" });
      loadStatusAtual();

      // Limpar campos se necessário
      if (selectedStatus === "inativo") {
        setTipoAtividade("");
        setGrupoSelecionado("");
        setDescricaoAtividade("");
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const souEu = responsavelAtual && responsavelAtual.id_militar === militar?.id;

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "ativo":
        return "default";
      case "em_atividade":
        return "secondary";
      case "inativo":
        return "outline";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "ativo":
        return "Ativo";
      case "em_atividade":
        return "Em Atividade";
      case "inativo":
        return "Inativo";
      default:
        return status;
    }
  };

  const handleEditarStatus = (registro: any) => {
    setEditandoMilitar(registro.id_militar);
    setEditStatus(registro.status);
    setEditTipoAtividade(registro.tipo_atividade || "");
    setEditGrupo(registro.id_grupo || "");
    setEditDescricao(registro.descricao_atividade || "");
  };

  const handleCancelarEdicao = () => {
    setEditandoMilitar(null);
    setEditStatus("");
    setEditTipoAtividade("");
    setEditGrupo("");
    setEditDescricao("");
  };

  const handleSalvarStatusMilitar = async (id_militar: string) => {
    if (!editStatus) {
      enqueueSnackbar("Selecione um status", { variant: "error" });
      return;
    }

    if (editStatus === "em_atividade" && !editTipoAtividade) {
      enqueueSnackbar("Selecione o tipo de atividade", { variant: "error" });
      return;
    }

    if (editTipoAtividade === "grupo" && !editGrupo) {
      enqueueSnackbar("Selecione um grupo", { variant: "error" });
      return;
    }

    if (editTipoAtividade === "outros" && !editDescricao) {
      enqueueSnackbar("Descreva a atividade", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await relatorioMovimentoOficialService.updateStatusMilitar(id_militar, {
        status: editStatus,
        tipo_atividade: editStatus === "em_atividade" ? editTipoAtividade : undefined,
        id_grupo: editTipoAtividade === "grupo" ? editGrupo : undefined,
        descricao_atividade: editTipoAtividade === "outros" ? editDescricao : undefined,
      });

      enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" });
      handleCancelarEdicao();
      loadTodosStatus();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Funções para mudança rápida de status
  const handleAbrirModalStatus = (militar: any, status: string) => {
    setMilitarSelecionado(militar);
    setStatusSelecionado(status);
    setTipoAtividadeModal("");
    setGrupoModal("");
    setDescricaoModal("");
    setModalStatusAberta(true);
  };

  const handleConfirmarStatus = async () => {
    if (!militarSelecionado) return;

    if (statusSelecionado === "em_atividade" && !tipoAtividadeModal) {
      enqueueSnackbar("Selecione o tipo de atividade", { variant: "error" });
      return;
    }

    if (tipoAtividadeModal === "grupo" && !grupoModal) {
      enqueueSnackbar("Selecione um grupo", { variant: "error" });
      return;
    }

    if (tipoAtividadeModal === "outros" && !descricaoModal.trim()) {
      enqueueSnackbar("Descreva a atividade", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await relatorioMovimentoOficialService.updateStatusMilitar(militarSelecionado.id_militar, {
        status: statusSelecionado,
        tipo_atividade: statusSelecionado === "em_atividade" ? tipoAtividadeModal : undefined,
        id_grupo: tipoAtividadeModal === "grupo" ? grupoModal : undefined,
        descricao_atividade: tipoAtividadeModal === "outros" ? descricaoModal : undefined,
      });

      enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" });
      setModalStatusAberta(false);
      setMilitarSelecionado(null);
      loadTodosStatus();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Sistema de Responsabilidade - Somente para Estado-Maior */}
      {canManageRMO && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Sistema de Responsabilidade - RMO
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingResponsavel ? (
              <SkeletonResponsavel />
            ) : (
              <div className="space-y-4">
                {responsavelAtual ? (
                  <FadeInWrapper>
                    <div className="flex items-start gap-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex-shrink-0">
                        <img
                          src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${responsavelAtual.militar?.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=0&size=l`}
                          alt={responsavelAtual.militar?.nick}
                          className="w-24 h-24 object-cover"
                          style={{ imageRendering: 'pixelated' }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Responsável Atual</p>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {responsavelAtual.militar?.patente?.abreviacao} {responsavelAtual.militar?.nick}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Desde: {moment(responsavelAtual.data_hora_inicio).format("DD/MM/YYYY HH:mm")}
                            </p>
                            {tempoResponsavel && (
                              <p className="text-sm font-mono text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                {tempoResponsavel}
                              </p>
                            )}
                          </div>
                          <Badge variant={responsavelAtual.status === "ativo" || responsavelAtual.status === "retomou" ? "default" : "secondary"}>
                            {responsavelAtual.status === "ativo" ? "Ativo" : responsavelAtual.status === "retomou" ? "Retomou" : "Pausado"}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {!souEu && (
                        <Button onClick={handlePegarRMO} disabled={loading}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Pegar RMO
                        </Button>
                      )}
                      {souEu && (
                        <>
                          {(responsavelAtual.status === "ativo" || responsavelAtual.status === "retomou") && (
                            <Button onClick={() => setModalPausarRMOAberta(true)} disabled={loading} variant="outline">
                              <Pause className="h-4 w-4 mr-2" />
                              Pausar RMO
                            </Button>
                          )}
                          {responsavelAtual.status === "pausado" && (
                            <Button onClick={handleRetomarRMO} disabled={loading} variant="outline">
                              <Play className="h-4 w-4 mr-2" />
                              Retomar RMO
                            </Button>
                          )}
                          <Button onClick={() => setModalFinalizarRMOAberta(true)} disabled={loading} variant="destructive">
                            <Square className="h-4 w-4 mr-2" />
                            Finalizar RMO
                          </Button>
                        </>
                      )}
                    </div>
                  </FadeInWrapper>
                ) : (
                  <>
                    <p className="text-muted-foreground">Nenhum responsável ativo no momento</p>
                    <Button onClick={handleIniciarRMO} disabled={loading}>
                      <Play className="h-4 w-4 mr-2" />
                      Iniciar RMO
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Toggle entre visão pessoal e gerencial */}
      {canManageRMO && (
        <div className="flex gap-2">
          <Button
            variant={viewMode === "manager" ? "default" : "outline"}
            onClick={() => setViewMode("manager")}
          >
            <Eye className="h-4 w-4 mr-2" />
            Visão Geral dos Oficiais
          </Button>
        </div>
      )}

      {/* Visão Gerencial - Estado-Maior */}
      {viewMode === "manager" && canManageRMO && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Status de Todos os Oficiais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {loadingVisaoGeral ? (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </>
              ) : todosStatus.length === 0 ? (
                <p className="col-span-full text-muted-foreground text-center py-8">
                  Nenhum oficial com status registrado
                </p>
              ) : (
                todosStatus.map((registro, index) => (
                  <StaggerWrapper key={registro.id_militar} index={index}>
                    <Card className="bg-gray-50 dark:bg-gray-800/50">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center gap-3">
                          {/* Habbo Imager cabeça */}
                          <img
                            src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${registro.militar?.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                            alt={registro.militar?.nick}
                            className="w-16 h-16 object-contain"
                            style={{ imageRendering: 'pixelated' }}
                          />

                        {/* Nome do oficial */}
                        <div className="text-center w-full">
                          <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                            {registro.militar?.patente?.abreviacao} {registro.militar?.nick}
                          </p>
                          {registro.tipo_atividade && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {registro.tipo_atividade === "grupo" && registro.grupo?.grupo}
                              {registro.tipo_atividade === "treinamento" && "Treinamento"}
                              {registro.tipo_atividade === "patrulha" && "Patrulha"}
                              {registro.tipo_atividade === "outros" && "Outros"}
                            </p>
                          )}
                          {registro.status === "em_atividade" && temposAtividade[registro.id_militar] && (
                            <p className="text-xs font-mono text-blue-600 dark:text-blue-400 flex items-center justify-center gap-1 mt-1">
                              <Clock className="h-3 w-3" />
                              {temposAtividade[registro.id_militar]}
                            </p>
                          )}
                        </div>

                        {/* Status atual */}
                        <Badge variant={getStatusBadgeVariant(registro.status)} className="w-full justify-center">
                          {getStatusLabel(registro.status)}
                        </Badge>

                        {/* Ícones de ação de status */}
                        <div className="flex gap-1 justify-center pt-2 border-t w-full">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAbrirModalStatus(registro, "ativo")}
                            className="flex-col h-auto py-2 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                            title="Marcar como Ativo"
                          >
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-xs mt-1">Ativo</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAbrirModalStatus(registro, "inativo")}
                            className="flex-col h-auto py-2 px-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Marcar como Inativo"
                          >
                            <UserX className="h-4 w-4" />
                            <span className="text-xs mt-1">Inativo</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAbrirModalStatus(registro, "em_atividade")}
                            className="flex-col h-auto py-2 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            title="Marcar Em Atividade"
                          >
                            <Loader2 className="h-4 w-4" />
                            <span className="text-xs mt-1">Atividade</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </StaggerWrapper>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de confirmação de mudança de status */}
      <Dialog open={modalStatusAberta} onOpenChange={setModalStatusAberta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Atualizar Status do Oficial</DialogTitle>
            <DialogDescription>
              {statusSelecionado === "ativo" && "Confirme para marcar o oficial como Ativo"}
              {statusSelecionado === "inativo" && "Confirme para marcar o oficial como Inativo"}
              {statusSelecionado === "em_atividade" && "Configure o tipo de atividade do oficial"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {militarSelecionado && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium">
                  {militarSelecionado.militar?.patente?.abreviacao} {militarSelecionado.militar?.nick}
                </p>
              </div>
            )}

            {statusSelecionado === "em_atividade" && (
              <>
                <SelectSingle
                  title="Tipo de Atividade"
                  value={tipoAtividadeModal}
                  onChange={(e) => setTipoAtividadeModal(e.target.value)}
                  required
                >
                  <option value="">Selecione o tipo</option>
                  {tipoAtividadeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectSingle>

                {tipoAtividadeModal === "grupo" && (
                  <SelectSingle
                    title="Grupo"
                    value={grupoModal}
                    onChange={(e) => setGrupoModal(e.target.value)}
                    required
                  >
                    <option value="">Selecione um grupo</option>
                    {grupos.map((g) => (
                      <option key={g.grupo.id} value={g.grupo.id}>
                        {g.grupo.grupo}
                      </option>
                    ))}
                  </SelectSingle>
                )}

                {tipoAtividadeModal === "outros" && (
                  <TextArea
                    title="Descrição da Atividade"
                    value={descricaoModal}
                    onChange={(e) => setDescricaoModal(e.target.value)}
                    required
                  />
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setModalStatusAberta(false);
                setMilitarSelecionado(null);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmarStatus} disabled={loading}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Pausar RMO */}
      <Dialog open={modalPausarRMOAberta} onOpenChange={setModalPausarRMOAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pausar RMO</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja pausar o Relatório de Movimento de Oficiais?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalPausarRMOAberta(false)}>
              Cancelar
            </Button>
            <Button onClick={() => { setModalPausarRMOAberta(false); handlePausarRMO(); }} disabled={loading}>
              Confirmar Pausa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Finalizar RMO */}
      <Dialog open={modalFinalizarRMOAberta} onOpenChange={setModalFinalizarRMOAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalizar RMO</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja finalizar o Relatório de Movimento de Oficiais? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFinalizarRMOAberta(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => { setModalFinalizarRMOAberta(false); handleFinalizarRMO(); }} disabled={loading}>
              Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
